import { ApplicationConfig, isDevMode } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import { HttpErrorResponse, HttpStatusCode, provideHttpClient, withInterceptors } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { provideStore } from '@ngrx/store';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';
import { UserEffects } from './data-services/stores/effects/user.effects';
import { CoreEffects } from './data-services/stores/effects/core.effects';
import { EventSystemEffects } from './data-services/stores/effects/event-systems.effects';
import { EventsEffects } from './data-services/stores/effects/events.effects';
import { PagesEffects } from './data-services/stores/effects/pages.effects';
import { FacebookLoginProvider, GoogleLoginProvider, MicrosoftLoginProvider, SocialAuthService, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { environment } from '../environments/environment';
import { guards } from './data-services/guards/lrptickets.guards';
import { interceptors } from './data-services/interceptors/lrptickets.interceptors';
import { AdminEffects } from './data-services/stores/effects/admin.effects';
import { ErrorHandlerService } from './data-services/handlers/error.handler';
import { NgcCookieConsentConfig, provideNgcCookieConsent } from 'ngx-cookieconsent';
import { LoggingService } from './data-services/logging.service';
import { provideNgxApiWrapper } from 'ngx-apiwrapper';
import { throwError } from 'rxjs';
import { AdminEventEffects } from './data-services/stores/effects/admin/events.effects';
import { cookieConfig, provideErrorHandler, provideRecaptcha, provideSocialAuth } from './app.providers';
import { appStoreReducer } from './data-services/stores/reducers/app.reducer';
import { AuthService } from './data-services/auth.service';

export const appConfig: ApplicationConfig = {
    providers: [
    provideErrorHandler,
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([...interceptors])),
    provideRouter(routes, withInMemoryScrolling({scrollPositionRestoration: 'top'})),
    providePrimeNG({
        theme: {
            preset: Aura
        }
    }),
    provideStore({
        app: appStoreReducer,
        router: routerReducer,
    }),
    provideRouterStore(),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideEffects(UserEffects, CoreEffects, EventSystemEffects, EventsEffects, PagesEffects, AdminEffects, AdminEventEffects),
    provideNgxApiWrapper({
        availableApis: {
            lrpTickets: environment.API,
            paypal: environment.paypal.PAYPAL_SANDBOX ? 'https://api-m.sandbox.paypal.com' : 'https://api-m.paypal.com'
        },
        defaultApi: 'lrpTickets',
        errorHandler: (err: HttpErrorResponse) => {
            let errorMessage = '';
            if (err.error instanceof ErrorEvent) {
                // A client-side or network error occurred
                errorMessage = `An error occurred: ${err.error.message}`;
            } else {
                // The backend returned an unsuccessful response code
                errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;
            }
            if (!environment.production){
                console.error(errorMessage, err.error);
            }
            let errorPath = '/error';
            if ([HttpStatusCode.Unauthorized,HttpStatusCode.Forbidden].includes(err.status)){
                // Not authorised path
                errorPath = '/login';
            }
            if (HttpStatusCode.NotFound === err.status){
                // Not found path
                errorPath = '/not-found';
            }

            // const router = inject(Router)
            // router.navigateByUrl(errorPath, {
            //     state: {
            //         path: err.url,
            //         status_code: err.status,
            //         message: err.message,
            //         trace: err.error,
            //     }
            // })
            return throwError(() => new Error(err.message))
        }
    }),
    provideNgcCookieConsent(cookieConfig),
    ...guards,
    AuthService,
    CommonModule,
    TooltipModule,
    MessageService,
    LoggingService,
    SocialAuthService,
    Document,
    provideRecaptcha,
    {
        // Social auth login
        provide: 'SocialAuthServiceConfig',
        useValue: {
            autoLogin: false,
            lang: 'en',
            providers: [
                {
                    id: GoogleLoginProvider.PROVIDER_ID,
                    provider: new GoogleLoginProvider(environment.google.GOOGLE_CLIENT_ID,
                        {
                            oneTapEnabled: false
                        }
                    )
                },
                {
                    id: FacebookLoginProvider.PROVIDER_ID,
                    provider: new FacebookLoginProvider(environment.facebook.FACEBOOK_APP_ID,
                        {
                            scopes: [],
                            return_scrops: true,
                            enable_profile_selector: true
                        }
                    )
                },
                {
                    id: MicrosoftLoginProvider.PROVIDER_ID,
                    provider: new MicrosoftLoginProvider(environment.microsoft.AD_CLIENT_ID,
                        {
                            redirect_uri: 'https://preview.lrptickets.co.uk'
                        }
                    ),
                },
            ],
            onError: (err) => console.error(err)
        } as SocialAuthServiceConfig
    }
    ],
};
